import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import i18next from 'i18next';
import Container from '../../StyledComponents/Container';
import { List } from '../../StyledComponents/Grid';
import { H2 } from '../../StyledComponents/Heading';
import theme from '../../StyledComponents/layout/theme';
import Loader from '../../StyledComponents/Loader';
import { Page, PageSection } from '../../StyledComponents/Page';
import { Columns, Column } from '../../StyledComponents/Columns';
import { Repeat } from '../../StyledComponents/Repeat';
import { Text } from '../../StyledComponents/Text';
import Tabs from '../../StyledComponents/Tabs';
import PubSub from '../../utils/PubSub';
import WaykeSearchItem from '../Item';
import WaykeItemProvider, { EcomSettings } from '../Item/Providers/WaykeItemProvider';
import { ConversionOption, SearchFilterNameTypes, SearchFilterTypes } from '../../@types/Search';
import { FILTER_NAME_TO_FACET_ID } from '../../Gql/adapter';
import { PublicApiProps, PublicApiSettings, resolvePublicApi } from '../../Gql/config';
import { migrateLegacyParams } from '../../Gql/legacyParams';
import { canonicalParams, FREE_TEXT } from '../../Gql/searchParams';
import { fetchSuggestions } from '../../Gql/suggest';
import useSiteSearch from '../../Gql/useSiteSearch';
import CarsItem from './CarsItem';
import ContentItem from './ContentItem';
import SearchFilters from './SearchFilters';
import { Cars, CMS } from './SearchModels';
import SearchResult from './SearchResult';
import useAnalytics from './useAnalytics';
import useMultiSearch from './useSearch';
import useTabs from './useTabs';
import { translateHits } from './Utils/Translation';
import { createUrl, decode, encode, pushState, sanitizeUrlSearchParams } from './Utils/url';
import Snackbar from '../../StyledComponents/Snackbar';
import SearchBar from '../SearchBar';

if (!window.WaykePubSub2) {
  window.WaykePubSub2 = PubSub;
}

// Any UUID: legacy ids are v4, ids from the public API are v7.
const regexGuid16 = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const getIdFromPathname = () => {
  const parts = window.location.pathname.split('/');

  const index = parts.indexOf('id');
  if (index === -1) return undefined;
  const id = parts?.[index + 1];
  return !!id && regexGuid16.test(id) ? id : undefined;
};

const replaceState = (nextUrl: string) => {
  if (nextUrl !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
    history.replaceState(undefined, '', nextUrl);
  }
};

/**
 * The address bar on first load, in the new grammar. Old links (`?qf=…`,
 * `?query=…`, legacy keys) are translated and the URL quietly rewritten so that
 * everything the page produces from here on is in one grammar.
 */
const readInitialParams = (publicApi?: PublicApiSettings) => {
  const current = new URLSearchParams(location.search);
  if (!publicApi) return current;
  const migrated = migrateLegacyParams(current);
  replaceState(createUrl(migrated));
  return migrated;
};

const DISABLED_PUBLIC_API: PublicApiSettings = {
  api: { url: '' },
  branchGroupId: '',
  market: 'SE',
};

interface SearchComponentProps {
  multiSearchUrl: string;
  vehicleSearchUrl: string;
  contentSearchUrl: string;
  displaySearchField?: boolean;
  filterList?: SearchFilterTypes[];
  conversionOptions?: ConversionOption[];
  searchPathname: string;
  querySuggestionUrl: string;
  placeholder?: string;
  evCalculatorUrl: string;
  evCalculatorEnabled?: boolean;
  publicApi?: PublicApiSettings;
}

const SearchComponent = ({
  multiSearchUrl,
  vehicleSearchUrl,
  contentSearchUrl,
  filterList,
  conversionOptions,
  displaySearchField,
  searchPathname,
  querySuggestionUrl,
  placeholder,
  evCalculatorUrl,
  evCalculatorEnabled,
  publicApi,
}: SearchComponentProps) => {
  const [vehicleId, setVehicleId] = useState<string | undefined>(getIdFromPathname);

  const [urlSearchParams, setUrlSearchParams] = useState(() => readInitialParams(publicApi));
  const latestParams = useRef(urlSearchParams);
  latestParams.current = urlSearchParams;
  const legacyQuery = urlSearchParams.get('query') || undefined;
  const qf = urlSearchParams.get('qf');
  const urlSearchParamsQf = qf ? new URLSearchParams(decode(qf)) : undefined;
  const qfQuery = urlSearchParamsQf?.get('query') || undefined;
  const query = publicApi ? urlSearchParams.get(FREE_TEXT) || undefined : legacyQuery;

  const [tab, setTab] = useTabs();
  const [legacyResult, legacyRequestState, legacyLoadMore] = useMultiSearch(
    urlSearchParams,
    {
      multiSearchUrl,
      vehicleSearchUrl,
      contentSearchUrl,
    },
    tab,
    !!vehicleId || !!publicApi
  );
  const [gqlResult, gqlRequestState, gqlLoadMore] = useSiteSearch(
    urlSearchParams,
    publicApi || DISABLED_PUBLIC_API,
    !!vehicleId || !publicApi
  );
  const result = publicApi ? gqlResult : legacyResult;
  const requestState = publicApi ? gqlRequestState : legacyRequestState;
  const loadMore = publicApi ? gqlLoadMore : legacyLoadMore;

  const qfMultiSearch = qf ? undefined : new URLSearchParams(result?.cars?.query.parameters || '');
  const filterParams = publicApi ? urlSearchParams : qfMultiSearch || urlSearchParamsQf;
  const cmsHits = result?.retailers_cms?.stats?.totalHits || 0;
  const carsHits = result?.cars?.stats?.totalHits || 0;
  const facetQueryString = result?.cars?.query.queryString || undefined;

  const displayTabs = !(!result || (!publicApi && qf) || !query || !cmsHits);

  const facetFilterList = useMemo(
    () =>
      publicApi
        ? filterList
            ?.map((f) => ({
              ...f,
              filterName: (FILTER_NAME_TO_FACET_ID[f.filterName] ||
                f.filterName) as SearchFilterNameTypes,
            }))
            .filter((f, i, all) => all.findIndex((x) => x.filterName === f.filterName) === i)
        : filterList,
    [filterList, publicApi]
  );

  useAnalytics(vehicleId);

  // A free text that is exactly a suggestion ("audi" → Audi, "volvo xc60" →
  // Volvo XC60) becomes that suggestion's filters, as the legacy search did
  // with its query-to-filter rewrite and web-ui-v2 does with suggest.
  useEffect(() => {
    if (!publicApi || !query) return;
    let active = true;
    fetchSuggestions(publicApi, query, 5)
      .then((response) => {
        if (!active) return;
        const match = response.suggestions.find(
          (s) => s.facet && s.label.localeCompare(query, undefined, { sensitivity: 'base' }) === 0
        );
        if (!match) return;
        // The address bar may have changed while the suggestions loaded.
        const next = new URLSearchParams(latestParams.current);
        next.delete(FREE_TEXT);
        new URLSearchParams(match.facet).forEach((value, key) => {
          if (!next.getAll(key).includes(value)) next.append(key, value);
        });
        const params = canonicalParams(next);
        replaceState(createUrl(params));
        setUrlSearchParams(params);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [publicApi, query]);

  useEffect(() => {
    window.WaykePubSub2.subscribe({
      eventName: 'onQueryUpdate',
      callback: (nextQuery: string) => {
        const raw = new URLSearchParams(nextQuery);
        raw.delete('qf');
        const params = publicApi ? migrateLegacyParams(raw) : raw;

        const nextUrl = createUrl(params);
        pushState(nextUrl);
        setUrlSearchParams(params);
      },
    });

    window.addEventListener('popstate', () => {
      const id = getIdFromPathname();
      if (id) {
        setVehicleId(id);
      } else {
        const params = new URLSearchParams(location.search);
        setUrlSearchParams(publicApi ? migrateLegacyParams(params) : params);
        setVehicleId(undefined);
      }
    });
  }, []);

  useEffect(() => {
    if (result?.cars && result?.retailers_cms) {
      const hash = location.hash.replace('#', '');
      if (!!cmsHits && hash === 'innehall') {
        onContent();
      } else if (!carsHits && !!cmsHits) {
        onContent();
      } else {
        onCars();
      }
    }
  }, [JSON.stringify(result)]);

  const onVehicleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string, slug: string) => {
    e.stopPropagation();
    e.preventDefault();
    pushState(`${itemHref}${id}/${slug}`);
    window.scroll(0, 0);
    setVehicleId(id);
  };

  const onCars = useCallback(() => {
    window.location.hash = '';
    setTab('cars');
  }, []);

  const onContent = useCallback(() => {
    setTab('retailers_cms');
    window.location.hash = 'innehall';
  }, []);

  const onFilterUpdate = (_query: string) => {
    if (publicApi) {
      // The panels emit query strings with whatever keys they were given;
      // the migration normalises them (query → q, drops hits/offset/page).
      const params = migrateLegacyParams(new URLSearchParams(_query));
      pushState(createUrl(params));
      setUrlSearchParams(params);
      return;
    }

    const __query = new URLSearchParams(_query);
    __query.delete('offset');
    const nextQuery = __query.toString();

    const encoded = encode(nextQuery);
    const sanitized = sanitizeUrlSearchParams(new URLSearchParams(nextQuery)).toString();
    const sanitizedAndEncoded = sanitized ? encode(sanitized) : '';
    const nextUrlSearchParamsLocal = new URLSearchParams(`qf=${encoded}`);
    const nextUrlSearchParams = new URLSearchParams(
      sanitizedAndEncoded ? `qf=${sanitizedAndEncoded}` : ''
    );

    const nextUrl = createUrl(nextUrlSearchParams);
    pushState(nextUrl);
    setUrlSearchParams(nextUrlSearchParamsLocal);
  };

  const itemHref = `${(searchPathname || '/sok').replace(/\/$/, '')}/id/`;
  const currentResult = tab === 'retailers_cms' ? result?.retailers_cms : result?.cars;
  const totalHitsText = translateHits(currentResult?.stats.totalHits, tab);

  const displayBranchName = useMemo(() => {
    const branches = result?.carFacets?.find(
      (x) => x.id === 'branch' || x.id === 'branchDisplayName'
    )?.filters.length;
    return branches !== undefined && branches > 1;
  }, [result?.carFacets]);

  return (
    <>
      {vehicleId ? (
        <WaykeSearchItem
          id={vehicleId}
          evCalculatorUrl={evCalculatorUrl}
          evCalculatorEnabled={evCalculatorEnabled}
          conversionOptions={conversionOptions}
          itemHref={itemHref}
          onClickSearchItem={onVehicleClick}
        />
      ) : (
        <Page>
          {displaySearchField && (
            <PageSection>
              <Container>
                <SearchBar
                  pathname={searchPathname}
                  url={querySuggestionUrl}
                  placeholder={placeholder}
                  publicApi={publicApi}
                />
              </Container>
            </PageSection>
          )}
          {query && (
            <PageSection>
              <Container>
                <H2 noMargin>
                  {i18next.t('search.searchResult')} ”{query}”
                </H2>
              </Container>
            </PageSection>
          )}
          {displayTabs && (
            <PageSection>
              <Container>
                <Tabs
                  tabs={[
                    {
                      name: 'cars',
                      displayName: i18next.t('search.types.cars'),
                      count: `${result?.cars?.stats?.totalHits || 0}`,
                      onClick: onCars,
                    },
                    {
                      name: 'retailers_cms',
                      displayName: i18next.t('search.types.cmsContent'),
                      count: `${result?.retailers_cms?.stats?.totalHits || 0}`,
                      onClick: onContent,
                    },
                  ]}
                  active={tab}
                />
              </Container>
            </PageSection>
          )}

          {result ? (
            <>
              <PageSection accent fillSpace>
                <Container>
                  <Repeat>
                    <Columns $spacing={2} valign="center">
                      <Column grow>
                        {tab !== 'retailers_cms' && (
                          <SearchFilters
                            loading={requestState.isLoading}
                            urlSearchParams={filterParams}
                            initialFacets={result.carFacets}
                            facets={result.cars?.facets || undefined}
                            totalHitsText={totalHitsText}
                            filterList={facetFilterList}
                            query={publicApi ? query : qfQuery || facetQueryString}
                            sortOptions={currentResult?.documentList.sortOptions}
                            onFilterUpdate={onFilterUpdate}
                          />
                        )}
                      </Column>
                      <Column noShrink>
                        <Text $tone="alt" $size="small">
                          {totalHitsText}
                        </Text>
                      </Column>
                    </Columns>
                  </Repeat>
                  <Repeat>
                    {requestState.isLoading && !requestState.isLoadingMore ? (
                      <Loader />
                    ) : (
                      <SearchResult
                        count={currentResult?.documentList.documents.length || 0}
                        totalHits={currentResult?.stats.totalHits || 0}
                        loadingMore={requestState.isLoadingMore}
                        loadMore={loadMore ? () => loadMore(tab) : undefined}
                      >
                        {currentResult?.documentList.documents.length ? (
                          <List>
                            {currentResult?.documentList.documents.map((d) =>
                              tab === 'retailers_cms' ? (
                                <ContentItem key={d._id} data={d as CMS} />
                              ) : (
                                <CarsItem
                                  key={d._id}
                                  data={d as Cars}
                                  href={itemHref}
                                  displayBranchName={displayBranchName}
                                  onClick={onVehicleClick}
                                />
                              )
                            )}
                          </List>
                        ) : (
                          <Snackbar
                            severity="warning"
                            icon
                            heading={
                              i18next.t('search.snackbarNoSearchResults.heading') || undefined
                            }
                          >
                            {i18next.t('search.snackbarNoSearchResults.body')}
                          </Snackbar>
                        )}
                      </SearchResult>
                    )}
                  </Repeat>
                </Container>
              </PageSection>
            </>
          ) : (
            <Loader />
          )}
        </Page>
      )}
    </>
  );
};

type SearchProps = Omit<SearchComponentProps, 'publicApi'> &
  PublicApiProps & {
    provider: {
      url: string;
      urlMlt?: string;
      graphQlUrl: string;
      evCalculatorUrl: string;
      apiKey?: string;
      ecomSettings?: EcomSettings;
      googleMapsApiKey?: string;
      googleMapsMarker?: string;
    };
  };

const Search = ({ provider, publicApiUrl, branchGroupId, apiKey, ...props }: SearchProps) => {
  const publicApi = useMemo(
    () =>
      resolvePublicApi({
        publicApiUrl,
        branchGroupId,
        apiKey: apiKey || provider.apiKey,
      }),
    [publicApiUrl, branchGroupId, apiKey, provider.apiKey]
  );
  return (
    <ThemeProvider theme={theme}>
      <WaykeItemProvider
        {...provider}
        publicApiUrl={publicApiUrl}
        branchGroupId={branchGroupId}
        apiKey={apiKey || provider.apiKey}
      >
        <SearchComponent {...props} publicApi={publicApi} />
      </WaykeItemProvider>
    </ThemeProvider>
  );
};

export default Search;
